import {
  createBrowserRouter,
  Navigate,
} from 'react-router-dom';

import NotFoundDisplay from './components/common/error/NotFoundDisplay.tsx';
import RootErrorBoundary from './components/common/error/RootErrorBoundary.tsx';
import { appLayoutLoader } from './appLayoutLoader.ts';
import WatchedAppLayout from './components/layout/WatchedAppLayout.tsx';
import { PublicLayout } from './PublicLayout.tsx';

const wrappedCreateBrowserRouter = createBrowserRouter;

const getPopupRoutes = () => [
  {
    path: '/campaigns',
    children: [
      {
        path: '/campaigns',
        lazy: () => import('@/screens/campaign/CampaignScreen.tsx'),
      },
      {
        path: '/campaigns/:campaignId/popups',
        lazy: () => import('@/screens/campaign/CampaignPopupsScreen.tsx'),
      },
      {
        path: '/campaigns/:campaignId/popups/:popupId/settings',
        lazy: () =>
          import('@/screens/campaign/CampaignPopupSettingsScreen.tsx'),
      },
      // {
      //   path: '/campaigns/:campaignId/popups/:popupId/editor',
      //   lazy: () =>
      //     import('@/screens/campaign/CampaignPopupEditorScreenPuck.tsx'),
      // },
      {
        path: '/campaigns/:campaignId/popups/:popupId/design',
        lazy: () =>
          import('@/screens/campaign/CampaignPopupEditorScreen.tsx'),
      },
      {
        path: '/campaigns/:campaignId/popups/:popupId/stats',
        lazy: () =>
          import('@/screens/campaign/CampaignPopupStatsScreen.tsx'),
      },
      {
        path: '/campaigns/:campaignId/subscribers',
        lazy: () =>
          import('@/screens/campaign/CampaignSubscribersScreen.tsx'),
      },
      {
        path: '/campaigns/:campaignId/stats',
        lazy: () =>
          import('@/screens/campaign/CampaignStatisticsScreen.tsx'),
      },
      {
        path: '/campaigns/:campaignId/conversions',
        lazy: () =>
          import('@/screens/campaign/CampaignConversionsScreen.tsx'),
      },
    ],
  },
  {
    path: '/subscribers',
    children: [
      {
        path: '/subscribers',
        lazy: () => import('@/screens/subscribers/SubscribersScreen.tsx'),
      },
    ],
  },
  {
    path: '/popups',
    children: [
      {
        path: '/popups',
        lazy: () => import('@/screens/popup/PopupScreen.tsx'),
      },
    ],
  },
  {
    path: '/statistics',
    lazy: () => import('@/screens/statistics/StatisticsScreen.tsx'),
  },
];

export const router = wrappedCreateBrowserRouter([
  {
    id: 'root',
    Component: WatchedAppLayout,
    loader: appLayoutLoader,
    errorElement: <RootErrorBoundary />,
    children: [
      {
        path: '/',
        element: <Navigate to="/dashboard" />,
      },
      {
        path: '/dashboard',
        lazy: () => import('@/screens/DashboardScreen.tsx'),
      },
      {
        path: '/docs',
        lazy: () => import('@/screens/DocsScreen.tsx'),
      },
      {
        path: '/library',
        children: [
          {
            path: '/library',
            lazy: () => import('@/screens/library/TemplateLibraryScreen.tsx'),
          },
        ],
      },
      {
        path: '/websites',
        children: [
          {
            path: '/websites',
            lazy: () => import('@/screens/website/WebsiteScreen.tsx'),
          },
          {
            path: '/websites/:websiteId/pages',
            lazy: () => import('@/screens/website/WebsitePagesScreen.tsx'),
          },
          {
            path: '/websites/pages/best',
            lazy: () =>
              import('@/screens/website/BestPerformingPagesScreen.tsx'),
          },
          {
            path: '/websites/pages/worst',
            lazy: () =>
              import('@/screens/website/WorstPerformingPagesScreen.tsx'),
          },
        ],
      },
      ...getPopupRoutes(),
      {
        path: '/account',
        lazy: () => import('@/screens/MyAccountScreen.tsx'),
      },
      {
        path: '/mw',
        children: [
          {
            path: '/mw/campaigns',
            children: [
              {
                path: '/mw/campaigns',
                lazy: () => import('@/screens/message-whale/campaign/MWCampaignsScreen.tsx'),
              },
              {
                path: '/mw/campaigns/:campaignId/edit',
                lazy: () => import('@/screens/message-whale/campaign/MWCampaignEditScreen2.tsx'),
              },
              {
                path: '/mw/campaigns/:campaignId/stats',
                lazy: () => import('@/screens/message-whale/campaign/MWCampaignStatScreen.tsx'),
              },
              {
                path: '/mw/campaigns/:campaignId/messages/:messageId',
                lazy: () => import('@/screens/message-whale/message/MWMessageEditScreen.tsx'),
              },
            ],
          },
          {
            path: '/mw/lists',
            children: [
              {
                path: '/mw/lists',
                lazy: () => import('@/screens/message-whale/list/MWListsScreen.tsx'),
              },
              {
                path: '/mw/lists/:listId/edit',
                lazy: () => import('@/screens/message-whale/list/MWListEditScreen.tsx'),
              },
            ],
          },
          {
            path: '/mw/channels',
            children: [
              {
                path: '/mw/channels',
                lazy: () => import('@/screens/message-whale/channel/MWChannelsScreen.tsx'),
              },
              {
                path: '/mw/channels/:channelId/edit',
                lazy: () => import('@/screens/message-whale/channel/MWChannelEditScreen.tsx'),
              },
            ],
          },
          {
            path: '/mw/segments',
            children: [
              {
                path: '/mw/segments',
                lazy: () => import('@/screens/message-whale/segment/MWSegmentsScreen.tsx'),
              },
              {
                path: '/mw/segments/:segmentId/edit',
                lazy: () => import('@/screens/message-whale/segment/MWSegmentEditScreen.tsx'),
              },
            ],
          },
          {
            path: '/mw/automations',
            children: [
              {
                path: '/mw/automations',
                lazy: () => import('@/screens/workflow/WorkflowListScreen.tsx'),
              },
              {
                path: '/mw/automations/:workflowId/editor',
                lazy: () => import('@/screens/workflow/WorkflowScreen.tsx'),
              },
            ],
          },
          {
            path: '/mw/inbox',
            children: [
              {
                path: '/mw/inbox',
                lazy: () => import('@/screens/message-whale/inbox/MWInboxScreen.tsx'),
              },
            ],
          },
          {
            path: '/mw/statistics',
            lazy: () => import('@/screens/message-whale/stat/MWStatScreen.tsx'),
          },
        ],
      },
      {
        path: '/organization',
        children: [
          {
            path: '/organization/settings',
            lazy: () => import('@/screens/organization/OrganizationSettingsScreen.tsx'),
          },
          {
            path: '/organization/team',
            lazy: () => import('@/screens/organization/TeamSettingsScreen.tsx'),
          },
          {
            path: '/organization/billing',
            lazy: () => import('@/screens/BillingScreen.tsx'),
          },
          {
            path: '/organization/usage',
            lazy: () => import('@/screens/organization/CreditsScreen.tsx'),
          },
        ],
      },
      {
        path: '/integration',
        children: [
          {
            path: '/integration/shoprenter',
            lazy: () => import('@/screens/integrations/Shoprenter/ShoprenterSettingsScreen.tsx'),
          },
          {
            path: '/integration/shopify',
            lazy: () => import('@/screens/integrations/Shopify/ShopifySettingsScreen.tsx'),
          },
        ],
      },
      {
        path: '/settings',
        lazy: () => import('@/screens/SettingsScreen.tsx'),
      },
      {
        path: '/ai-informations',
        lazy: () => import('@/screens/AIMarketingScreen.tsx'),
      },
      {
        path: '/support',
        lazy: () => import('@/screens/SupportScreen.tsx'),
      },
      {
        path: '/onboarding',
        lazy: () => import('@/screens/onboarding/OnboardingScreen.tsx'),
      },
      // {
      //   path: '/root',
      //   children: [
      //     {
      //       path: '/root/pre-registrations',
      //       lazy: () => import('@/screens/pre-registrations/PreRegistrationsScreen.tsx'),
      //     },
      //     {
      //       path: '/root/pre-registrations/:id',
      //       lazy: () => import('@/screens/pre-registrations/PreRegistrationDetailsScreen.tsx'),
      //     },
      //     {
      //       path: '/root/blog/posts',
      //       lazy: () => import('@/screens/root/blog/BlogPosts.tsx'),
      //     },
      //     {
      //       path: '/root/blog/posts/:id',
      //       lazy: () => import('@/screens/pre-registrations/PreRegistrationsScreen.tsx'),
      //     },
      //     {
      //       path: '/root/blog/categories/',
      //       lazy: () => import('@/screens/pre-registrations/PreRegistrationsScreen.tsx'),
      //     },
      //     {
      //       path: '/root/blog/categories/:id',
      //       lazy: () => import('@/screens/pre-registrations/PreRegistrationsScreen.tsx'),
      //     },
      //   ],
      // },
      {
        path: '*',
        element: <NotFoundDisplay />,
      },
    ],
  },
  {
    Component: PublicLayout,
    errorElement: <RootErrorBoundary />,
    children: [
      {
        path: '/login',
        lazy: () => import('@/screens/LoginScreen.tsx'),
      },
      {
        path: '/registration',
        lazy: () => import('@/screens/RegistrationScreen'),
      },
      {
        path: '/registration-success',
        lazy: () => import('@/screens/RegistrationSuccessScreen'),
      },
      {
        path: '/forgotten',
        lazy: () => import('@/screens/ForgottenPasswordScreen'),
      },
      {
        path: '/reset-password',
        lazy: () => import('@/screens/ResetPasswordScreen.tsx'),
      },
      {
        path: '/accept-invitation',
        lazy: () => import('@/screens/public/invitations/AcceptInvitation.tsx'),
      },
      {
        path: '/verify',
        lazy: () => import('@/screens/VerifyRegistrationScreen.tsx'),
      },
      {
        path: '/integrations/shoprenter/:shopName',
        lazy: () => import('@/screens/integrations/Shoprenter/ShoprenterCallbackScreen.tsx'),
      },
      {
        path: '/integrations/shopify/:shopName',
        lazy: () => import('@/screens/integrations/Shopify/ShopifyCallbackScreen.tsx'),
      },
    ],
  }
  // TODO: create a 404 page
]);
